import React from "react";
import Grid from "@material-ui/core/Grid";
import { FieldArray, getIn } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";
import { useDispatch } from "react-redux";

import {
  AutoSaveField as Field,
  Input,
  SelectAutocomplete,
  DatePicker,
  InlineRadioGroup,
  TimePicker
} from "../../../../components/Form";
import {
  initialVisitDetails,
  MAX_DATE_OF_VISIT_LENGTH,
  NO_VISIT_REASONS,
  REMARKS_LENGTH
} from "../../constants";
import { Divider } from "../Divider";
import {
  getInvalidMessage,
  getMinDateMessage,
  getRequiredMessage
} from "../../../../utils/getValidationMessage";
import { useStyles } from "./styled";
import { YesNoNaList } from "../../../../constants/options";
import { Subtitle } from "../../../../components/Subtitle";
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from "../../../../constants";
import { LinkButton } from "../../../../components/Buttons/LinkButton";
import { AddButton } from "../../../../components/Buttons/AddButton";
import { updateProspect } from "../../../../store/actions/appConfig";
import { YES_NO_OPTIONS } from "../../../CompanyStakeholders/constants";
import { ErrorMessage } from "../../../../components/Notifications";

export const visitDetailFieldsSchema = () => ({
  isVisitConducted: Yup.string().nullable(),
  reasonForNoVisit: Yup.string()
    .nullable()
    .when("isVisitConducted", {
      is: "no",
      then: Yup.string().required(getRequiredMessage("Reason for No Site Visit"))
    }),
  visitDetails: Yup.array().of(
    Yup.object().shape({
      kycVisitDate: Yup.date()
        .min(new Date(1990, 0, 1), getMinDateMessage("Date of Site Visit"))
        .max(new Date(), getMinDateMessage("Date of Site Visit"))
        .typeError(getInvalidMessage("Date of Site Visit")),
      kycVisitTime: Yup.string()
        .nullable()
        .test("is-valid-time", getInvalidMessage("Time of Site Visit"), value => {
          if (!value) return true;
          const timeRegex = /^(0[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i;
          try {
            const formattedTime = format(new Date(value), TIME_FORMAT);
            return timeRegex.test(formattedTime);
          } catch (error) {
            return false;
          }
        }),
      visitConductedBy: Yup.string().nullable(),
      agentName: Yup.string().nullable(),
      visitConductedAt: Yup.string().nullable(),
      evidenceOfVisit: Yup.string().nullable(),
      noticeToCounterfeit: Yup.string()
        .nullable()
        .when("$isVisitConducted", {
          is: "yes",
          then: Yup.string().required(
            getRequiredMessage("Did you notice counterfeit product at this time of visit")
          ),
          otherwise: Yup.string().nullable()
        }),
      remarks: Yup.string()
        .nullable()
        .max(REMARKS_LENGTH, "Maximum ${max} characters allowed")
    })
  )
});

export const VisitDetailFields = ({ values, setFieldValue, dsaChecklistDoc, siteVisitReport }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  const changeTimeProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(new Date(value), DATE_TIME_FORMAT) };

  const addDateOfVisitHandler = arrayHelper => () => {
    arrayHelper.push({ ...initialVisitDetails[0] });
  };

  const isAdditionalDateOfVisitDisabled = (values, index) => {
    return !(
      getIn(values, `visitDetails[${index}].kycVisitDate`, false) &&
      getIn(values, `visitDetails[${index}].kycVisitTime`, false) &&
      getIn(values, `visitDetails[${index}].visitConductedBy`, false) &&
      getIn(values, `visitDetails[${index}].agentName`, false) &&
      getIn(values, `visitDetails[${index}].visitConductedAt`, false) &&
      getIn(values, `visitDetails[${index}].noticeToCounterfeit`, false)
    );
  };

  const handleDelete = (index, values, setFieldValue) => {
    let newValues = values.visitDetails.filter((_, i) => index !== i);
    newValues = newValues.length ? newValues : [...initialVisitDetails];
    setFieldValue("visitDetails", newValues);
    dispatch(
      updateProspect({
        "prospect.kycAnnexure.visitDetails": newValues
      })
    );
  };

  return (
    <>
      <Divider />
      <Subtitle title="Site Visit details" classes={{ wrapper: classes.subtitleBranch }} />
      <Grid>
        <Field
          name="isVisitConducted"
          component={InlineRadioGroup}
          path="prospect.kycAnnexure.isVisitConducted"
          options={YesNoNaList}
          label="Is site visit conducted? "
        />
      </Grid>
      {values.isVisitConducted === "yes" &&
        (dsaChecklistDoc?.uploadStatus?.toLowerCase() !== "uploaded" &&
          siteVisitReport?.uploadStatus?.toLowerCase() !== "uploaded" && (
            <div style={{ marginBottom: 20 }}>
              <ErrorMessage error="Please upload DSA Meeting Report/ Site Visit Report*" />
            </div>
          ))}
      {values.isVisitConducted === "yes" && (
        <FieldArray name="visitDetails">
          {arrayHelpers => (
            <Grid container spacing={3}>
              {values.visitDetails.map((item, index) => {
                const prospectPath = `prospect.kycAnnexure.visitDetails[${index}]`;
                const isDisplayDeleteButton = values.visitDetails.length > 1;
                return (
                  <React.Fragment key={index}>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].kycVisitDate`}
                        path={`${prospectPath}.kycVisitDate`}
                        label="Date of Site Visit"
                        component={DatePicker}
                        tabIndex="0"
                        changeProspect={changeDateProspectHandler}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].kycVisitTime`}
                        path={`${prospectPath}.kycVisitTime`}
                        label="Time of Site Visit"
                        component={TimePicker}
                        InputProps={{
                          inputProps: { tabIndex: 0 }
                        }}
                        changeProspect={changeTimeProspectHandler}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].visitConductedBy`}
                        path={`${prospectPath}.visitConductedBy`}
                        label="Site Visit Conducted By"
                        component={SelectAutocomplete}
                        datalistId={"visitConductedBy"}
                        tabIndex="0"
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].agentName`}
                        path={`${prospectPath}.agentName`}
                        label="Site Visit Conducted Name"
                        component={SelectAutocomplete}
                        datalistId={
                          values.visitDetails[index].visitConductedBy === "AGENCY"
                            ? "visitConductedAgency"
                            : "visitConductedRoRm"
                        }
                        tabIndex="0"
                        disabled={!values.visitDetails[index].visitConductedBy}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].visitConductedAt`}
                        path={`${prospectPath}.visitConductedAt`}
                        label="Site Visit Conducted At"
                        component={SelectAutocomplete}
                        datalistId={"visitConductedAt"}
                        tabIndex="0"
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`visitDetails[${index}].evidenceOfVisit`}
                        path={`${prospectPath}.evidenceOfVisit`}
                        label="Evidence of Site Visit"
                        component={SelectAutocomplete}
                        options={YES_NO_OPTIONS}
                        tabIndex="0"
                      />
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Field
                        name={`visitDetails[${index}].noticeToCounterfeit`}
                        component={InlineRadioGroup}
                        path={`${prospectPath}.noticeToCounterfeit`}
                        options={YES_NO_OPTIONS}
                        label="Did you notice counterfeit products at the time of visit(applicable where meeting at business premises)"
                        InputProps={{
                          inputProps: { tabIndex: 0 }
                        }}
                      />
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Field
                        name={`visitDetails[${index}].remarks`}
                        path={`${prospectPath}.remarks`}
                        label="Remarks/ Observations (if any)"
                        component={Input}
                        multiline
                        rows="4"
                        InputProps={{
                          inputProps: {
                            maxLength: REMARKS_LENGTH,
                            tabIndex: 0
                          }
                        }}
                      />
                    </Grid>
                    <Grid item sm={isDisplayDeleteButton ? 12 : 0} xs={12} container>
                      {isDisplayDeleteButton && (
                        <LinkButton
                          className={classes.deleteButton}
                          clickHandler={() => handleDelete(index, values, setFieldValue)}
                          title="Delete"
                          data-testid="delete-visit-detail"
                        />
                      )}
                    </Grid>
                    {values.visitDetails.length === index + 1 &&
                      values.visitDetails.length < MAX_DATE_OF_VISIT_LENGTH && (
                        <Grid item sm={12} xs={12} style={{ marginTop: "10px" }}>
                          <AddButton
                            data-testid="add-visit-detail"
                            title="Add another Site Visit Detail"
                            onClick={addDateOfVisitHandler(arrayHelpers)}
                            disabled={isAdditionalDateOfVisitDisabled(values, index)}
                          />
                        </Grid>
                      )}
                    {values.visitDetails.length - 1 > index && (
                      <Grid item sm={12} xs={12}>
                        <Divider />
                      </Grid>
                    )}
                  </React.Fragment>
                );
              })}
            </Grid>
          )}
        </FieldArray>
      )}
      {values.isVisitConducted === "no" && (
        <Grid container spacing={3}>
          <Grid item sm={12} xs={12}>
            <Field
              name="reasonForNoVisit"
              path="prospect.kycAnnexure.reasonForNoVisit"
              label="Reason for not conducting site visit"
              component={SelectAutocomplete}
              options={NO_VISIT_REASONS}
            />
          </Grid>
        </Grid>
      )}

      <Grid item sm={12} xs={12}>
        <Divider />
      </Grid>
    </>
  );
};

VisitDetailFields.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  dsaChecklistDoc: PropTypes.shape({
    uploadStatus: PropTypes.string
  }),
  siteVisitReport: PropTypes.shape({
    uploadStatus: PropTypes.string
  })
};

VisitDetailFields.defaultProps = {
  dsaChecklistDoc: undefined,
  siteVisitReport: undefined
};
